import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { companyService, companyCommentService, userService } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { useAuthToken } from '../hooks/useAuthToken';
import BillingManagement from '../components/BillingManagement';
import InvoiceActions from '../components/InvoiceActions';
import CompanyComments from '../components/CompanyComments';
import ConfirmDialog from '../components/ConfirmDialog';
import { LoadingState, ErrorState, isNotFound, serverMessage } from '../components/PageState';
import { planLabel } from '../components/PlanFilter';
import { parsePlainDate, toDateInputValue, daysUntil, frequencyLabel } from '../components/company/companyFormat';
import { useLeaveGuard } from '../components/company/companyHooks';
import { buildBillingChangeNote, describeBillingChanges } from '../utils/billingNotes';
import { formatDate, formatNumber, plural, EMPTY } from '../utils/format';

// The billing form's fields, from a company as the API returns it
const toBillingValues = (company) => ({
  accountType: company.accountType || '',
  paymentFrequency: company.paymentFrequency || '',
  renewalDate: toDateInputValue(company.renewalDate),
  preferredPointOfContact: company.preferredPointOfContact || '',
  freeUsers: company.freeUsers || 0,
  paidUsers: company.paidUsers || 0,
  overduePayment: company.overduePayment || false,
  usesStripe: company.usesStripe || false,
});

const EMPTY_BILLING = toBillingValues({});

// "Renews 1 Aug 2026, in 23 days", or how long ago a renewal date passed
const renewalText = (value) => {
  const date = parsePlainDate(value);
  const days = daysUntil(value);
  if (!date || days === null) return EMPTY;
  const formatted = formatDate(date);
  if (days === 0) return `Renews ${formatted}, today`;
  if (days > 0) return `Renews ${formatted}, in ${plural(days, 'day')}`;
  return `Renewal was due ${formatted}, ${plural(-days, 'day')} ago`;
};

const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const userName = (user) => `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || 'Unnamed user';

// A user as a link to their record, with their email beside the name
const UserLink = ({ user }) => (
  <>
    <Link to={`/users/${user.id}`}>{userName(user)}</Link>
    {user.email && userName(user) !== user.email && <span className="text-muted ms-2 text-break">{user.email}</span>}
  </>
);

const CRMCompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const refreshToken = useAuthToken();
  const { canEdit } = usePermissions();

  const [company, setCompany] = useState(null);
  const [editedCompany, setEditedCompany] = useState(EMPTY_BILLING);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [commentError, setCommentError] = useState(null);

  // Unsaved billing edits are easy to lose here, so leaving the page asks first
  const billingDirty = canEdit && !!company && describeBillingChanges(company, editedCompany).length > 0;
  const blocker = useLeaveGuard(billingDirty);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      await refreshToken();
      const response = await companyService.getCompany(id);
      setCompany(response.data);
      setEditedCompany(toBillingValues(response.data));
    } catch (err) {
      console.error('Failed to fetch company:', err);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      await refreshToken();
      const response = await userService.getUsersByCompany(id);
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsersError(err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      setCommentError(null);
      await refreshToken();
      const response = await companyCommentService.getComments(id);
      setComments(response.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setCommentError('Could not load the comments. Try again.');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setIsPostingComment(true);
      setCommentError(null);
      await refreshToken();
      const response = await companyCommentService.createComment(id, newComment.trim());
      setComments((prev) => [...prev, response.data]);
      setNewComment('');
    } catch (err) {
      setCommentError(serverMessage(err, err.message || 'Could not add the comment'));
      console.error(err);
    } finally {
      setIsPostingComment(false);
    }
  };

  useEffect(() => {
    setSaveStatus(null);
    fetchCompany();
    fetchUsers();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFieldChange = (field, value) => {
    setSaveStatus(null);
    setEditedCompany((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddInvoiceComment = async (commentText) => {
    await refreshToken();
    await companyCommentService.createComment(id, commentText);
    fetchComments();
  };

  const handleSaveCompany = async () => {
    try {
      setIsSaving(true);
      setSaveStatus(null);
      await refreshToken();

      const billingNote = buildBillingChangeNote(company, editedCompany);

      const response = await companyService.updateCompany(id, { ...company, ...editedCompany });
      setCompany(response.data);
      setEditedCompany(toBillingValues(response.data));

      if (!billingNote) {
        setSaveStatus({ type: 'success', message: 'Saved' });
        return;
      }
      try {
        await companyCommentService.createComment(id, billingNote);
        setSaveStatus({ type: 'success', message: 'Saved' });
        fetchComments();
      } catch (noteErr) {
        console.error('Failed to record billing change note', noteErr);
        setSaveStatus({ type: 'warning', message: 'Saved, but the change note could not be recorded' });
      }
    } catch (err) {
      setSaveStatus({ type: 'error', message: serverMessage(err, 'Could not save the billing changes. Try again.') });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Back to wherever this page was opened from (Home, Recent notes, the company page), else CRM
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/crm');
  };

  const backButton = (
    <Button variant="outline-secondary" onClick={goBack}>
      <ArrowLeft size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />Back
    </Button>
  );

  if (loading || !company) {
    return (
      <Container fluid className="py-4">
        <div className="mb-3">{backButton}</div>
        {loading ? (
          <LoadingState label="Loading company" />
        ) : isNotFound(loadError) ? (
          <Alert variant="warning">Company not found</Alert>
        ) : (
          <ErrorState message={serverMessage(loadError, 'Could not load this company.')} onRetry={fetchCompany} />
        )}
      </Container>
    );
  }

  const admins = users
    .filter((user) => user.role === 'COMPANY_ADMIN' && !user.deleted)
    .sort((a, b) => userName(a).localeCompare(userName(b)));
  const firstUser = users.find((user) => user.firstToSignup);
  const contact = company.preferredPointOfContact?.trim();
  const billing = [company.usesStripe ? 'Stripe' : 'Manual', frequencyLabel(company.paymentFrequency)]
    .filter(Boolean)
    .join(', ');

  // A line for the user-based rows while the company's users load or if they failed
  const usersPlaceholder = usersLoading ? (
    <span className="text-muted">Loading...</span>
  ) : usersError ? (
    <span className="text-muted">
      Could not load the users.{' '}
      <Button variant="link" size="sm" className="p-0 align-baseline" onClick={fetchUsers}>Try again</Button>
    </span>
  ) : null;

  return (
    <Container fluid className="py-4">
      <ConfirmDialog
        show={blocker.state === 'blocked'}
        title="Leave without saving?"
        confirmLabel="Leave page"
        variant="danger"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      >
        <p className="mb-0">Your changes to the billing for {company.name} have not been saved.</p>
      </ConfirmDialog>

      <Row className="mb-4">
        <Col>
          <div className="mb-3">{backButton}</div>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <h1 className="mb-0">{company.name}</h1>
            {company.overduePayment && <Badge bg="danger">Overdue</Badge>}
            <Badge bg={company.enabled ? 'success' : 'secondary'}>
              {company.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </Col>
        <Col xs="auto" className="d-flex align-items-end pb-1">
          <Button as={Link} to={`/companies/${id}`} variant="outline-primary">
            View company page
          </Button>
        </Col>
      </Row>

      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Account</h5>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={6}>
              <dl className="kv-list">
                <dt>Plan</dt>
                <dd>{company.accountType ? planLabel(company.accountType) : EMPTY}</dd>
                <dt>Seats</dt>
                <dd>{formatNumber(company.paidUsers || 0)} paid, {formatNumber(company.freeUsers || 0)} free</dd>
                <dt>Users</dt>
                <dd>{company.totalUsers != null ? plural(company.totalUsers, 'user') : EMPTY}</dd>
                <dt>Billing</dt>
                <dd>{billing}</dd>
                <dt>Renewal</dt>
                <dd>{renewalText(company.renewalDate)}</dd>
                <dt>Customer since</dt>
                <dd>{formatDate(company.createdAt)}</dd>
              </dl>
            </Col>
            <Col lg={6}>
              <dl className="kv-list">
                <dt>Point of contact</dt>
                <dd>
                  {!contact ? EMPTY : looksLikeEmail(contact) ? <a href={`mailto:${contact}`}>{contact}</a> : contact}
                </dd>
                <dt>First to sign up</dt>
                <dd>{usersPlaceholder || (firstUser ? <UserLink user={firstUser} /> : EMPTY)}</dd>
                <dt>Company admins</dt>
                <dd>
                  {usersPlaceholder || (admins.length === 0 ? 'None' : (
                    <ul className="list-unstyled mb-0">
                      {admins.map((admin) => (
                        <li key={admin.id}><UserLink user={admin} /></li>
                      ))}
                    </ul>
                  ))}
                </dd>
              </dl>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <BillingManagement
        values={editedCompany}
        savedValues={company}
        onFieldChange={handleFieldChange}
        onSave={handleSaveCompany}
        isSaving={isSaving}
        saveStatus={saveStatus}
        canEdit={canEdit}
      />

      <CompanyComments
        comments={comments}
        commentsLoading={commentsLoading}
        commentError={commentError}
        onDismissError={() => setCommentError(null)}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
        isPostingComment={isPostingComment}
        canEdit={canEdit}
        headerActions={canEdit ? <InvoiceActions compact canEdit={canEdit} onAddComment={handleAddInvoiceComment} /> : null}
      />
    </Container>
  );
};

export default CRMCompanyDetail;
